import { StopContext, TrainIdentifier } from "@vygruppen/vy-train-map";
import { TrainEventTypeEnum } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainEventTypeEnum";
import { TrainStaffOnDuty } from "features/VehicleSidebar/VehicleList/TrainList/types";
import { Nullish } from "shared/utils/objectUtils";

type AltTransStopId = {
  id: string;
  type: StopContext;
};

type AltTransStopReference = {
  plcId: Nullish<string>;
  jbvId: string;
  stopIds: Array<AltTransStopId>;
};

export type AltTransTrainStation = {
  reference: AltTransStopReference;
  name?: string | null;
};

export type AltTransTrainId = Omit<TrainIdentifier, "identifier"> & {
  id: string;
};

export type AltTransTrainRoute = {
  originStop: AltTransTrainStation;
  destinationStop: AltTransTrainStation;
  currentStop?: AltTransTrainStation | null;
  currentStopArrivalTime?: string | null;
};

export enum AltTransIncidentArrivalStatus {
  APPROACHING_INCIDENT = "APPROACHING_INCIDENT",
  AT_INCIDENT = "AT_INCIDENT",
  DEPARTED_INCIDENT = "DEPARTED_INCIDENT",
  UNKNOWN = "UNKNOWN",
}

export type AltTransIncidentArrival = {
  scheduledTime?: string | null;
  estimatedTime?: string | null;
  stop: AltTransTrainStation;
  status: AltTransIncidentArrivalStatus;
};

type AltTransPassengerCount = {
  incidentStretchPassengerCount?: number | null;
};

export type AltTransTrainEvent = {
  id: string;
  label: string;
  type: TrainEventTypeEnum;
};

export enum AltTransMeasureType {
  NEXT_TRAIN = "NEXT_TRAIN",
  NO_COVERAGE = "NO_COVERAGE",
  OTHER_PROVIDER_REFERRAL = "OTHER_PROVIDER_REFERRAL",
  OTHER_TRAINS = "OTHER_TRAINS",
  REPLACEMENT_TRIP = "REPLACEMENT_TRIP",
  SHUTTLE = "SHUTTLE",
}

export type AltTransMeasure = {
  type: AltTransMeasureType;
};

export type AltTransIncidentTrain = {
  id: AltTransTrainId;
  trainRoute: AltTransTrainRoute;
  incidentArrival: AltTransIncidentArrival;
  passengerCount: AltTransPassengerCount;
  events: Array<AltTransTrainEvent>;
  measures: Array<AltTransMeasure>;
  trainStaffOnDuty: Array<TrainStaffOnDuty>;
};

type AltTransTrainsByEntryPoint = {
  entryPoint: AltTransTrainStation;
  trains: Array<AltTransIncidentTrain>;
};

export type AltTransIncidentTasks = {
  incidentId: string;
  trainsOutsideIncident: Array<AltTransTrainsByEntryPoint>;
  trainsAtIncident: Array<AltTransIncidentTrain>;
};

type Prediction = {
  fromTime: string;
  passengersRequiringTransport: number;
  predictionBlockMinutes: number;
};

type MeasureStretch = {
  fromStop: AltTransStopReference;
  toStop: AltTransStopReference;
  predictions: Prediction[];
};

export type PassengerPredictionsResponse = {
  incidentStretchPredictions: Prediction[];
  measureStretches: MeasureStretch[];
};

export type AltTransMeasureProgress = {
  measureStretchCoverage: Array<AltTransStretchCoverage>;
  incidentStretchCoverage: Nullish<AltTransStretchCoverage>;
  totalCoverage: AltTransBusCoverage;
  totalPassengersRequiringTransport: Nullish<number>;
};

export type AltTransStretchCoverage = {
  totalStretchCoverage: AltTransBusCoverage;
  maxPassengers: number;
  fromStation: AltTransStopReference;
  toStation: AltTransStopReference;
  stretchName: string;
  busCoverageBlocks: Array<AltTransCoverageTimeBlock>;
};

type AltTransCoverageTimeBlock = {
  fromTime: string;
  predictionBlockMinutes: number;
  busCoverage: AltTransBusCoverage;
};

export type AltTransBusCoverage = {
  totalRequired: Nullish<number>;
  totalRequiredByMeasureCard: Nullish<number>;
  totalOrdered: number;
  totalConfirmed: number;
  totalOperational: number;
  stillRequired: Nullish<number>;
};

export type AltTransIncidentMeasurePlanStatus =
  | "NOT_PUBLISHED"
  | "PUBLISHED"
  | "RETRACTED";
